import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, RefreshCw, Phone, Mail, Users, CalendarDays, Inbox, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "bg-transparent border border-border rounded-none h-12 px-4 w-full focus:outline-none focus:border-primary transition-colors duration-300 font-light text-foreground";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem("tp_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [campaignFilter, setCampaignFilter] = useState("all");

  const fetchLeads = async (t) => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${t || token}` },
      });
      setLeads(data.leads);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("tp_admin_token");
        setToken(null);
      } else {
        setError("Couldn't load leads. Try refreshing.");
      }
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (token) fetchLeads();
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      localStorage.setItem("tp_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem("tp_admin_token");
    setToken(null);
    setLeads(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 font-body">
        <form onSubmit={login} className="w-full max-w-md bg-card border border-border p-10" data-testid="admin-login-form">
          <img src="/trekpanda-mark.png" alt="TrekPanda" className="h-14 w-auto mx-auto mb-6" />
          <h1 className="font-heading font-light text-4xl text-center text-foreground mb-2" data-testid="admin-login-heading">
            Leads <em className="text-primary">admin</em>
          </h1>
          <p className="text-center text-sm font-light text-muted-foreground mb-8">Sign in to see campaign enquiries</p>
          <div className="space-y-5">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Admin email"
              className={inputCls}
              data-testid="admin-email-input"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className={inputCls}
              data-testid="admin-password-input"
            />
          </div>
          {error && (
            <p className="mt-4 text-sm text-red-700 text-center" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="mt-8 w-full bg-primary text-primary-foreground h-12 text-sm font-semibold uppercase tracking-[0.2em] hover:bg-[#2E9440] transition-colors duration-300 flex items-center justify-center"
            data-testid="admin-login-button"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  const campaigns = leads ? [...new Set(leads.map((l) => l.campaign || "bhutan"))] : [];
  const visible = leads ? (campaignFilter === "all" ? leads : leads.filter((l) => (l.campaign || "bhutan") === campaignFilter)) : [];

  return (
    <div className="min-h-screen bg-background font-body" data-testid="admin-dashboard">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <img src="/trekpanda-mark.png" alt="TrekPanda" className="h-9 w-auto" />
            <div>
              <h1 className="font-heading text-2xl font-medium text-foreground leading-none">Campaign leads</h1>
              <p className="text-xs text-muted-foreground mt-1" data-testid="leads-count">
                {leads ? `${visible.length} ${visible.length === 1 ? "enquiry" : "enquiries"}` : "Loading…"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchLeads()}
              className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] border border-border px-4 py-2.5 text-foreground hover:bg-secondary transition-colors duration-300"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] bg-primary text-primary-foreground px-4 py-2.5 hover:bg-[#2E9440] transition-colors duration-300"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {campaigns.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="campaign-filter">
            <Globe className="w-4 h-4 text-muted-foreground" />
            {["all", ...campaigns].map((c) => (
              <button
                key={c}
                onClick={() => setCampaignFilter(c)}
                className={`text-[10px] uppercase tracking-[0.2em] font-semibold px-4 py-2 border transition-colors duration-300 ${
                  campaignFilter === c ? "bg-primary text-primary-foreground border-primary" : "border-border text-foreground hover:bg-secondary"
                }`}
                data-testid={`campaign-filter-${c}`}
              >
                {c === "all" ? "All campaigns" : c}
              </button>
            ))}
          </div>
        )}

        {!leads ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-24" data-testid="leads-empty">
            <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
            <p className="font-heading text-2xl text-foreground">No enquiries yet</p>
            <p className="text-sm font-light text-muted-foreground mt-2">New form submissions will appear here instantly.</p>
          </div>
        ) : (
          <div className="border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]" data-testid="leads-table">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-5 py-4 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Received</th>
                  <th className="px-5 py-4 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Campaign</th>
                  <th className="px-5 py-4 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Name</th>
                  <th className="px-5 py-4 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Phone</th>
                  <th className="px-5 py-4 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Email</th>
                  <th className="px-5 py-4 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Travel date</th>
                  <th className="px-5 py-4 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Travellers</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((lead, i) => (
                  <tr key={lead.lead_id || i} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors duration-200" data-testid={`lead-row-${i}`}>
                    <td className="px-5 py-4 font-light text-muted-foreground whitespace-nowrap">{fmtDate(lead.created_at)}</td>
                    <td className="px-5 py-4">
                      <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-primary border border-primary/30 px-2.5 py-1" data-testid={`lead-campaign-${i}`}>
                        {lead.campaign || "bhutan"}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-medium text-foreground">{lead.name}</td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-primary hover:underline">
                        <Phone className="w-3.5 h-3.5" /> {lead.phone}
                      </a>
                    </td>
                    <td className="px-5 py-4">
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-foreground/80 hover:text-primary">
                        <Mail className="w-3.5 h-3.5" /> {lead.email}
                      </a>
                    </td>
                    <td className="px-5 py-4 font-light whitespace-nowrap">
                      <span className="flex items-center gap-2">
                        <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
                        {lead.travel_date || "Flexible"}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-light">
                      <span className="flex items-center gap-2">
                        <Users className="w-3.5 h-3.5 text-muted-foreground" />
                        {lead.travellers || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
